// frontend/src/socket.js
import { io } from 'socket.io-client';
import { getBackendUrl } from './utils/urlUtils';

const hasActiveSession = () => {
  try {
    const storedUser = localStorage.getItem('user');
    const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
    if (!storedUser || !authToken) return false;

    const parsedUser = JSON.parse(storedUser);
    return Boolean(parsedUser && (parsedUser.id || parsedUser._id || parsedUser.username));
  } catch (error) {
    return false;
  }
};

const getCurrentUser = () => {
  try {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  } catch (error) {
    console.error('[Socket.IO] Failed to parse user from localStorage:', error);
    return null;
  }
};

// Tự động detect connection URL dựa trên environment
const getSocketURL = () => {
  if (process.env.REACT_APP_SOCKET_URL) {
    return process.env.REACT_APP_SOCKET_URL;
  }

  try {
    // Ưu tiên backend URL dùng chung với API để tránh sai domain.
    return getBackendUrl();
  } catch (error) {
    console.warn('[Socket.IO] Failed to resolve backend URL from urlUtils:', error);
  }

  const protocol = window.location.protocol;
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//localhost:5000`;
  }

  return `${protocol}//${hostname}`;
};

const socketURL = getSocketURL();
console.log('[Socket.IO] Endpoint:', socketURL);

// Create shared socket instance. Chỉ connect khi có session.
const socket = io(socketURL, {
  withCredentials: true,
  transports: ['websocket', 'polling'],
  path: '/socket.io',
  reconnectionAttempts: 5,
  reconnectionDelay: 1000,
  timeout: 20000,
  autoConnect: false
});

const joinUserRooms = () => {
  const user = getCurrentUser();
  if (!user) return;

  const userId = user.id || user._id;
  if (userId) {
    socket.emit('join', userId);
  }

  if (user.role) {
    socket.emit('joinRole', user.role);
  }

  console.log('[Socket.IO] Joined rooms for user:', user.username || user.fullName || userId);
};

const syncSocketWithSession = () => {
  if (!hasActiveSession()) {
    if (socket.connected || socket.active) {
      socket.disconnect();
    }
    return;
  }

  if (!socket.connected && !socket.active) {
    socket.connect();
    return;
  }

  if (socket.connected) {
    joinUserRooms();
  }
};

// Add socket event listeners for debugging
socket.on('connect', () => {
  console.log('Socket.IO connected successfully with ID:', socket.id);
  joinUserRooms();
});

socket.on('connect_error', (error) => {
  if (hasActiveSession()) {
    console.error('Socket.IO connection error:', error.message);
  } else {
    console.log('[Socket.IO] Skipped connection error logging (no active session)');
  }
});

socket.on('disconnect', (reason) => {
  console.log('Socket.IO disconnected:', reason);
});

socket.on('reconnect_attempt', (attemptNumber) => {
  if (hasActiveSession()) {
    console.log(`Socket.IO reconnection attempt #${attemptNumber}`);
  }
});

// Track page changes
export const trackPageChange = (path, title) => {
  if (!hasActiveSession()) return;

  if (socket && socket.connected) {
    socket.emit('message', JSON.stringify({
      type: 'page_change',
      path,
      title
    }));
  }
};

// Initialize page tracking
if (typeof window !== 'undefined') {
  syncSocketWithSession();

  window.addEventListener('authChange', syncSocketWithSession);
  window.addEventListener('storage', (event) => {
    if (!event || event.key === null || ['user', 'token', 'authToken'].includes(event.key)) {
      syncSocketWithSession();
    }
  });

  // Track initial page load
  trackPageChange(window.location.pathname, document.title);

  // Set up history change listener for SPA navigation
  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;

  history.pushState = function pushStatePatched() {
    originalPushState.apply(this, arguments);
    setTimeout(() => {
      trackPageChange(window.location.pathname, document.title);
    }, 100);
  };

  history.replaceState = function replaceStatePatched() {
    originalReplaceState.apply(this, arguments);
    setTimeout(() => {
      trackPageChange(window.location.pathname, document.title);
    }, 100);
  };

  // Listen for popstate events (back/forward navigation)
  window.addEventListener('popstate', () => {
    setTimeout(() => {
      trackPageChange(window.location.pathname, document.title);
    }, 100);
  });
}

export default socket;
